import { useEffect, useState } from "react";
import { http } from "../api";
import { Users, Trophy, Sparkles, Send, X } from "lucide-react";
import { Modal, PageHeader, ImportExportBar, AddButton, FormRow, EmptyState } from "../shared";

const empty = { name: "", mobile: "", ward: 7, booth: "42", role: "Karyakarta", availability: "Weekends", skills: "", points: 0, active: true };
const roles = ["Booth President", "Ward Coordinator", "Karyakarta", "Mahila Karyakarta", "Yuva Karyakarta"];

export default function Volunteers() {
  const [rows, setRows] = useState([]);
  const [show, setShow] = useState(false);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [coach, setCoach] = useState(null);
  const [coachBusy, setCoachBusy] = useState(false);
  const [sendStatus, setSendStatus] = useState("");

  const load = () => http.get("/volunteers").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try { await http.post("/volunteers", { ...form, ward: Number(form.ward), points: Number(form.points) }); setShow(false); setForm(empty); load(); }
    finally { setSaving(false); }
  };

  const openCoach = async (v) => {
    setCoach({ volunteer: v, loading: true });
    setSendStatus("");
    try {
      const r = await http.get(`/volunteers/${v.volunteer_id}/coach`);
      setCoach(r.data);
    } catch (e) {
      setCoach({ volunteer: v, error: "Failed to load coach card" });
    }
  };

  const sendCoach = async () => {
    if (!coach?.volunteer) return;
    setCoachBusy(true); setSendStatus("");
    try {
      const r = await http.post(`/volunteers/${coach.volunteer.volunteer_id}/coach/send`);
      setSendStatus(`${r.data.status} · ${r.data.language.toUpperCase()} · ${r.data.message.slice(0, 80)}…`);
    } catch (e) {
      setSendStatus("Send failed. Check volunteer mobile.");
    } finally {
      setCoachBusy(false);
    }
  };

  return (
    <>
      <PageHeader eyebrow="TEAM ROSTER" title="Volunteers" subtitle={`${rows.length} karyakartas across Ward 7`}
        actions={<>
          <a href="/tv" target="_blank" rel="noreferrer" className="outline" data-testid="tv-link-button" style={{ textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 6, padding: "10px 15px", border: "1px solid var(--line)", background: "white", fontSize: 12, textTransform: "uppercase", letterSpacing: ".3px", color: "var(--ink)" }}>
            🖥 Open TV leaderboard
          </a>
          <ImportExportBar entity="volunteers" onImported={load} />
          <AddButton onClick={() => setShow(true)} label="Add volunteer" testid="volunteers-add-button" />
        </>} />

      <section className="panel">
        {rows.length === 0 ? <EmptyState icon={Users} title="No volunteers" hint="Add karyakartas to your roster." /> : (
          <div className="volunteer-table">
            <div className="tr th"><span>#</span><span>Karyakarta</span><span>Role & availability</span><span>Booth</span><span>Points</span><span>Coach</span></div>
            {rows.map((v, i) => (
              <div key={v.volunteer_id} className="tr" data-testid={`volunteer-row-${i}`}>
                <span className="rank">{i === 0 ? <Trophy size={16} color="#efb64e" /> : `#${i + 1}`}</span>
                <span className="person">
                  <span className="avatar person-avatar">{v.name.split(" ").map((x) => x[0]).join("")}</span>
                  <span><b>{v.name}</b><small>{v.mobile} · {v.skills}</small></span>
                </span>
                <span>{v.role}<br /><small>{v.availability}</small></span>
                <span>Ward {v.ward} · Booth {v.booth}</span>
                <span><b>{v.points}</b> pts {!v.active && <small className="hint">· inactive</small>}</span>
                <span>
                  <button className="outline" data-testid={`coach-open-${i}`} onClick={() => openCoach(v)} style={{ fontSize: 11, padding: "6px 10px" }}>
                    <Sparkles size={12} /> Card
                  </button>
                </span>
              </div>
            ))}
          </div>
        )}
      </section>

      {show && (
        <Modal title="Add volunteer" onClose={() => setShow(false)} testid="volunteer-modal">
          <div className="form-grid">
            <FormRow label="Name"><input data-testid="form-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></FormRow>
            <FormRow label="Mobile"><input data-testid="form-mobile-input" value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} /></FormRow>
            <FormRow label="Role"><select data-testid="form-role-input" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>{roles.map((r) => <option key={r}>{r}</option>)}</select></FormRow>
            <FormRow label="Availability"><input data-testid="form-avail-input" value={form.availability} onChange={(e) => setForm({ ...form, availability: e.target.value })} /></FormRow>
            <FormRow label="Booth"><input data-testid="form-booth-input" value={form.booth} onChange={(e) => setForm({ ...form, booth: e.target.value })} /></FormRow>
            <FormRow label="Points"><input data-testid="form-points-input" type="number" value={form.points} onChange={(e) => setForm({ ...form, points: e.target.value })} /></FormRow>
          </div>
          <FormRow label="Skills"><input data-testid="form-skills-input" value={form.skills} onChange={(e) => setForm({ ...form, skills: e.target.value })} placeholder="e.g. Field visits, Photography" /></FormRow>
          <div className="form-actions">
            <button className="outline" data-testid="form-cancel-button" onClick={() => setShow(false)}>Cancel</button>
            <button className="primary" data-testid="form-save-button" disabled={saving || !form.name} onClick={save}>{saving ? "Saving…" : "Save"}</button>
          </div>
        </Modal>
      )}
      {coach && (
        <Modal title={`${coach.volunteer?.name || "Volunteer"} · Weekly coach card`} onClose={() => setCoach(null)} testid="coach-modal">
          {coach.loading && <p className="hint">Loading personalized card…</p>}
          {coach.error && <p className="hint" style={{ color: "#b04a3f" }}>{coach.error}</p>}
          {coach.rank && (
            <>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 12, marginTop: 6 }} data-testid="coach-stats">
                <div style={{ background: "#fff", border: "1px solid var(--line)", borderRadius: 12, padding: 14 }} data-testid="coach-rank">
                  <p className="eyebrow" style={{ margin: 0 }}>RANK</p>
                  <strong style={{ fontSize: 28, display: "block", margin: "4px 0" }}>#{coach.rank}</strong>
                  <small style={{ color: "var(--muted)" }}>of {coach.total} karyakartas</small>
                </div>
                <div style={{ background: "#fff", border: "1px solid var(--line)", borderRadius: 12, padding: 14 }} data-testid="coach-points">
                  <p className="eyebrow" style={{ margin: 0 }}>POINTS</p>
                  <strong style={{ fontSize: 28, display: "block", margin: "4px 0" }}>{coach.volunteer?.points || 0}</strong>
                  <small style={{ color: "var(--muted)" }}>Streak: {coach.streak}</small>
                </div>
                <div style={{ background: "#fff", border: "1px solid var(--line)", borderRadius: 12, padding: 14 }} data-testid="coach-visits">
                  <p className="eyebrow" style={{ margin: 0 }}>VISITS (7d)</p>
                  <strong style={{ fontSize: 28, display: "block", margin: "4px 0" }}>{coach.visits_week}</strong>
                  <small style={{ color: "var(--muted)" }}>field visits logged</small>
                </div>
                <div style={{ background: "#fff", border: "1px solid var(--line)", borderRadius: 12, padding: 14 }} data-testid="coach-complaints">
                  <p className="eyebrow" style={{ margin: 0 }}>OPEN CASES</p>
                  <strong style={{ fontSize: 28, display: "block", margin: "4px 0" }}>{coach.complaints_owned}</strong>
                  <small style={{ color: "var(--muted)" }}>{coach.complaints_solved_week} solved this week</small>
                </div>
              </div>
              <div style={{ background: "linear-gradient(120deg,#0f766e0f,#f0bd611a)", border: "1px solid var(--line)", borderRadius: 14, padding: 18, marginTop: 14 }} data-testid="coach-ask">
                <p className="eyebrow">THIS WEEK'S ASK</p>
                <h3 style={{ margin: "6px 0" }}>{coach.ask_en}</h3>
                <p style={{ fontSize: 13, color: "var(--muted)", margin: 0 }}>{coach.ask_bn}</p>
              </div>
              <div className="form-actions" style={{ marginTop: 14 }}>
                <button className="outline" data-testid="coach-close" onClick={() => setCoach(null)}><X size={14} /> Close</button>
                <button className="primary" data-testid="coach-send" disabled={coachBusy || !coach.volunteer?.mobile} onClick={sendCoach}>
                  <Send size={14} /> {coachBusy ? "Sending…" : "Send SMS to karyakarta"}
                </button>
              </div>
              {sendStatus && <small className="ai-hint" data-testid="coach-send-status" style={{ display: "block", marginTop: 8 }}>{sendStatus}</small>}
              {!coach.volunteer?.mobile && <small className="hint" style={{ display: "block", marginTop: 8 }}>No mobile on file — cannot send SMS.</small>}
            </>
          )}
        </Modal>
      )}
    </>
  );
}
